/**
 * Inhaltsdaten: laden, typisieren, prüfen.
 *
 * Die JSON-Dateien unter `data/` sind die Quelle. Dieser Loader macht daraus
 * typisierte, indizierte Strukturen und prüft beim Start die Verweise —
 * ein Gegner, der auf ein nicht existierendes Geschoss zeigt, soll beim ersten
 * Test auffallen und nicht mitten im Spiel.
 *
 * Bewusst ohne Schema-Bibliothek: Die wertvollen Fehler sind hier gebrochene
 * VERWEISE und unsortierte Stufen, nicht falsche Feldtypen. Genau darauf zielt
 * `pruefeInhalte()`. (Abweichung von docs/2026-07-25-architektur.md §2 — dort
 * ist Zod vorgesehen; kommt, sobald die Inhaltsmenge es rechtfertigt.)
 */

import gegnerRoh from '../data/gegner.json' with { type: 'json' };
import faehigkeitenRoh from '../data/faehigkeiten.json' with { type: 'json' };
import affixeRoh from '../data/affixe.json' with { type: 'json' };
import basenRoh from '../data/basen.json' with { type: 'json' };
import regelwerkRoh from '../data/regelwerk.json' with { type: 'json' };

// ---------------------------------------------------------------- Typen

export type Slot = 'waffe' | 'ruestung' | 'umhang' | 'ring';
export type Schadensart = 'wucht' | 'glut';
export type Verhalten = 'stuermer' | 'umkreiser' | 'schuetze' | 'ausklang';
export type Beuteklasse = 'gering' | 'mittel' | 'hoch' | 'ausklang';

/**
 * Gegnerrang. Bestimmt Werte, Merkmalszahl und Beute — und ist der Grund,
 * warum sich eine Gruppe nicht mehr wie Trash anfuehlt (siehe
 * docs/2026-07-26-ueberarbeitung.md, B6/B7).
 */
export type Rang = 'gewoehnlich' | 'vorarbeiter' | 'gezeichneter' | 'ausklang';

export const RAENGE: readonly Rang[] = ['gewoehnlich', 'vorarbeiter', 'gezeichneter', 'ausklang'];

/** Rangordnung fuer Vergleiche ("mindestens Vorarbeiter"). */
export function rangStufe(r: Rang): number {
  return RAENGE.indexOf(r);
}

export type MerkmalArt = 'aura' | 'leben' | 'tempo' | 'minderung' | 'tod-spaltet' | 'entzug';

export interface Merkmal {
  id: string;
  name: string;
  art: MerkmalArt;
  schaden?: number;
  radius?: number;
  faktor?: number;
  anteil?: number;
  anzahl?: number;
}

export interface RangDef {
  name: string;
  leben: number;
  schaden: number;
  erfahrung: number;
  merkmale: number;
  radius: number;
  gefolge?: [number, number];
}

/**
 * Alle Stellschrauben. Kommt aus data/regelwerk.json — der Code liest, er setzt nicht.
 * Wer eine Zahl aendern will, aendert sie dort.
 */
export interface Regelwerk {
  spieler: {
    leben: { basis: number; jeStufe: number };
    vorrat: { basis: number; jeStufe: number };
    schaden: { basis: number; jeStufe: number };
    tempo: number;
    radius: number;
    vorratRegen: number;
    lebenRegenAnteil: number;
    lebenRegenAbSekunden: number;
    kritchanceBasis: number;
    kritschadenBasis: number;
  };
  erfahrung: { basis: number; exponent: number; hoechststufe: number };
  faehigkeiten: {
    plaetze: number;
    startFaehigkeit: string;
    freischaltStufen: number[];
    angeboteJeFreischaltung: number;
    handfaehigkeitenSchweigenGanz: boolean;
    standardAutomatikgrad: string;
    bewegungssprungSicherheit: {
      nurWennSpielerSteuert: boolean;
      nurInSpielerrichtung: boolean;
      nurAufBegehbarenBoden: boolean;
      nichtInDichtereGruppe: boolean;
      gruppenradius: number;
    };
  };
  karte: {
    grundflaeche: { basis: number; jeTiefe: number; hoechstens: number };
    kammern: { basis: number; jeZwoelfTiefen: number; hoechstens: number };
    gangbreite: { min: number; max: number };
    hallen: { anzahl: number; abTiefe: number };
    kammerFormen: Array<{ b: number; h: number; gewicht: number }>;
    halleFormen: Array<{ b: number; h: number; gewicht: number }>;
  };
  gegner: {
    dichteFlaecheJeGegner: { basis: number; jeTiefe: number; mindestens: number };
    gewoehnlichLebenFaktor: number;
    tiefeLebenJeStufe: number;
    tiefeSchadenJeStufe: number;
    gradLeben: number;
    gradSchaden: number;
    gradBeute: number;
    raenge: Record<Rang, RangDef>;
    aggro: { wecken: number; vergessen: number; weckenAnfuehrer: number; weckRuf: number };
    vorarbeiterAbTiefe: number;
    vorarbeiterJeAbstieg: { mindestens: number; jeKammernRest: number };
    gezeichneterAbTiefe: number;
    gezeichneterJeAbstieg: { mindestens: number; hoechstens: number };
    merkmale: Merkmal[];
  };
  beute: {
    chanceJeRang: Record<Rang, number>;
    stueckeJeRang: Record<Rang, number>;
    seltenheitsschubJeRang: Record<Rang, number>;
    fladen: {
      chance: number;
      chanceBeiNot: number;
      notSchwelle: number;
      heilungAnteil: number;
      lebensdauerSekunden: number;
    };
    gradFaktorDeckel: number;
    goldJeGegner: { min: number; max: number; jeTiefe: number };
    spuersinnDeckel: number;
    spuersinnAbnahmeAb: number;
    spuersinnAbnahmeFaktor: number;
  };
  kampf: {
    widerstandsdeckel: number;
    ruestungHaerte: { basis: number; jeTiefe: number; deckel: number };
    sichtweite: number;
    nahkampfgrenze: number;
    brandTaktSekunden: number;
  };
  begleiter: {
    sammelRadius: number;
    wartetSekunden: number;
    mindestabstandSpieler: number;
    korbMax: number;
  };
  abstieg: { foerderkorbRadius: number };
  ansicht: {
    zoomstufen: Record<string, { hochkant: number; quer: number }>;
    standardZoom: string;
  };
  tempoProfile: {
    standard: string;
    profile: Record<string, TempoProfil>;
  };
}

/**
 * Faktoren auf die Grundwerte. Zwei Gangarten desselben Spiels — damit sich
 * „zügig" gegen „gemessen" vergleichen lässt, ohne dass jemand dreißig Zahlen
 * von Hand dreht.
 */
export interface TempoProfil {
  name: string;
  beschreibung: string;
  spielerTempo: number;
  gegnerTempo: number;
  abklingen: number;
  wirkdauer: number;
  schaden: number;
  angriffstakt: number;
  aufhebeRadius: number;
}

export interface Gestalt {
  segmente: number;
  grundform: string;
  hoehe: number;
  breite: number;
}

export interface Gegner {
  id: string;
  name: string;
  /** Akkusativform fuer Aufgabentexte ("den Schmelzer"). Ohne sie steht in der
   *  Aufgabe "erschlage Der Schmelzer" — der Nominativ mitten im Satz. */
  akkusativ?: string;
  /** Ab welcher Fundtiefe diese Art ueberhaupt auftaucht. Staffelt die Vielfalt. */
  abTiefe?: number;
  /** Relative Haeufigkeit beim Setzen. Schwere Einheiten sind selten, nicht verboten. */
  spawngewicht?: number;
  beschreibung: string;
  leben: number;
  schaden: number;
  tempo: number;
  radius: number;
  angriffsreichweite: number;
  angriffstakt: number;
  telegraf: number;
  flaechenangriff?: number;
  verhalten: Verhalten;
  geschoss?: string;
  erfahrung: number;
  beuteklasse: Beuteklasse;
  elite?: boolean;
  benannt?: boolean;
  gestalt: Gestalt;
}

export interface Geschoss {
  id: string;
  name: string;
  tempo: number;
  radius: number;
  lebensdauer: number;
}

export type Ausloeser =
  | 'gegner-in-reichweite'
  | 'gegner-ausser-nahkampf'
  | 'leben-faellt'
  | 'elite-in-sicht'
  | 'immer';

export type Bedingung = 'mindestens-gegner' | 'unter-anteil' | 'mindestens-rang';

export interface Regel {
  ausloeser: Ausloeser;
  bedingung?: Bedingung;
  wert?: number;
  /**
   * Nur bei bedingung 'mindestens-rang': ab welcher Gegnerqualität die Regel
   * greift. Damit feuert eine starke Fähigkeit nicht mehr in Trash
   * (Ueberarbeitung B9).
   */
  rang?: Rang;
  prioritaet: number;
}

export type Wirkform = 'bogen' | 'ring' | 'schub' | 'geschoss' | 'selbst';

/** Wozu eine Fähigkeit da ist. Quer zu den Wegen — beides zusammen ergibt Identität. */
export type Faehigkeitskategorie = 'angriff' | 'mobilitaet' | 'verteidigung';

export interface Faehigkeit {
  id: string;
  name: string;
  kategorie?: Faehigkeitskategorie;
  /** Kann als Grundangriff auf Platz 1 stehen (dauerhaft, kostenlos). */
  grundangriff?: boolean;
  /**
   * Darf ueberhaupt automatisch feuern. FALSE fuer alles, was den Spieler
   * anhaelt oder versetzt — sonst nimmt die Automatik ihm die Bewegung weg.
   */
  automatikfaehig?: boolean;
  /** Ab welcher Stufe sie ueberhaupt angeboten werden darf. */
  abStufe?: number;
  klasse: string;
  weg: string;
  beschreibung: string;
  form: Wirkform;
  schaden?: number;
  schadensart?: Schadensart;
  reichweite?: number;
  winkel?: number;
  abklingen: number;
  vorrat: number;
  wirkdauer: number;
  wegdruecken?: number;
  eigenbewegung?: number;
  geschoss?: string;
  brennt?: { schaden: number; dauer: number };
  staerkung?: { schaden?: number; schadensminderung?: number; dauer: number };
  standardregel: Regel;
}

export type Wirkungsart = 'flach' | 'anteil';
export type Wertname =
  | 'schaden'
  | 'leben'
  | 'ruestung'
  | 'tempo'
  | 'vorrat'
  | 'widerstand-glut'
  | 'spuersinn'
  | 'kritchance'
  | 'kritschaden'
  | 'lebensentzug';

export interface AffixStufe {
  abPraegelevel: number;
  min: number;
  max: number;
  wortmarke: string;
}

export interface Affix {
  id: string;
  art: 'praefix' | 'suffix';
  gruppe: string;
  wirkung: { wert: Wertname; art: Wirkungsart };
  gewicht: number;
  slots: Slot[];
  stufen: AffixStufe[];
}

export interface SeltenheitDef {
  id: string;
  name: string;
  affixeMin: number;
  affixeMax: number;
  faelltAufBoden: boolean;
  gewicht: number;
  praefixeMax?: number;
  suffixeMax?: number;
  einzelstueck?: boolean;
}

export interface Basis {
  id: string;
  name: string;
  slot: Slot;
  basisstufe: number;
  grundwerte: Partial<Record<Wertname | 'wirktakt', number>>;
  fassungenMax: number;
  bindung: number;
  gewicht: number;
}

export interface FesterWert {
  wert: Wertname;
  art: Wirkungsart;
  wert_min: number;
  wert_max: number;
}

export interface Gepraegtes {
  id: string;
  name: string;
  basis: string;
  basisstufe: number;
  beschreibung: string;
  fest: FesterWert[];
  wild: FesterWert;
  gewicht: number;
}

export interface Inhalte {
  regelwerk: Regelwerk;
  merkmale: ReadonlyMap<string, Merkmal>;
  gegner: ReadonlyMap<string, Gegner>;
  gegnerListe: readonly Gegner[];
  geschosse: ReadonlyMap<string, Geschoss>;
  faehigkeiten: ReadonlyMap<string, Faehigkeit>;
  praefixe: readonly Affix[];
  suffixe: readonly Affix[];
  affixe: ReadonlyMap<string, Affix>;
  seltenheiten: readonly SeltenheitDef[];
  basen: readonly Basis[];
  basenNachSlot: ReadonlyMap<Slot, readonly Basis[]>;
  gepraegte: readonly Gepraegtes[];
}

// ---------------------------------------------------------------- Laden

function stufen(roh: unknown[][]): AffixStufe[] {
  return roh.map((t) => ({
    abPraegelevel: t[0] as number,
    min: t[1] as number,
    max: t[2] as number,
    wortmarke: t[3] as string,
  }));
}

function affixe(roh: readonly unknown[], art: 'praefix' | 'suffix'): Affix[] {
  return (roh as Array<Record<string, unknown>>).map((a) => ({
    id: a['id'] as string,
    art,
    gruppe: a['gruppe'] as string,
    wirkung: a['wirkung'] as Affix['wirkung'],
    gewicht: a['gewicht'] as number,
    slots: a['slots'] as Slot[],
    stufen: stufen(a['stufen'] as unknown[][]),
  }));
}

function karte<T extends { id: string }>(liste: readonly T[]): Map<string, T> {
  const m = new Map<string, T>();
  for (const e of liste) m.set(e.id, e);
  return m;
}

let zwischenspeicher: Inhalte | null = null;

export function ladeInhalte(): Inhalte {
  if (zwischenspeicher) return zwischenspeicher;

  const gegnerListe = gegnerRoh.gegner as unknown as Gegner[];
  const geschosseListe = gegnerRoh.geschosse as unknown as Geschoss[];
  const faehigkeitenListe = faehigkeitenRoh.faehigkeiten as unknown as Faehigkeit[];
  const praefixe = affixe(affixeRoh.praefixe, 'praefix');
  const suffixe = affixe(affixeRoh.suffixe, 'suffix');
  const seltenheiten = basenRoh.seltenheiten as unknown as SeltenheitDef[];
  const basen = basenRoh.basen as unknown as Basis[];
  const gepraegte = basenRoh.gepraegte as unknown as Gepraegtes[];

  const basenNachSlot = new Map<Slot, Basis[]>();
  for (const b of basen) {
    const liste = basenNachSlot.get(b.slot) ?? [];
    liste.push(b);
    basenNachSlot.set(b.slot, liste);
  }

  const regelwerk = regelwerkRoh as unknown as Regelwerk;

  zwischenspeicher = {
    regelwerk,
    merkmale: karte(regelwerk.gegner.merkmale),
    gegner: karte(gegnerListe),
    gegnerListe,
    geschosse: karte(geschosseListe),
    faehigkeiten: karte(faehigkeitenListe),
    praefixe,
    suffixe,
    affixe: karte([...praefixe, ...suffixe]),
    seltenheiten,
    basen,
    basenNachSlot,
    gepraegte,
  };

  const fehler = pruefeInhalte(zwischenspeicher);
  if (fehler.length > 0) {
    zwischenspeicher = null;
    throw new Error('Inhaltsdaten fehlerhaft:\n  ' + fehler.join('\n  '));
  }

  return zwischenspeicher;
}

// ---------------------------------------------------------------- Prüfen

/** Gibt eine Liste von Fehlermeldungen zurück. Leer = alles in Ordnung. */
export function pruefeInhalte(i: Inhalte): string[] {
  const fehler: string[] = [];

  const doppelt = (was: string, ids: readonly string[]): void => {
    const gesehen = new Set<string>();
    for (const id of ids) {
      if (gesehen.has(id)) fehler.push(`${was}: doppelte id "${id}"`);
      gesehen.add(id);
    }
  };

  doppelt('gegner', i.gegnerListe.map((g) => g.id));
  doppelt('basen', i.basen.map((b) => b.id));
  doppelt('affixe', [...i.praefixe, ...i.suffixe].map((a) => a.id));
  doppelt('gepraegte', i.gepraegte.map((g) => g.id));

  for (const g of i.gegnerListe) {
    if (g.geschoss && !i.geschosse.has(g.geschoss)) {
      fehler.push(`gegner "${g.id}": unbekanntes Geschoss "${g.geschoss}"`);
    }
    if (g.verhalten === 'schuetze' && !g.geschoss) {
      fehler.push(`gegner "${g.id}": Verhalten "schuetze" ohne Geschoss`);
    }
    if (g.leben <= 0 || g.tempo < 0 || g.radius <= 0) {
      fehler.push(`gegner "${g.id}": unplausible Grundwerte`);
    }
    // Zahlen-Kanon §12.4: Telegrafie großer Angriffe >= 700 ms
    if (g.flaechenangriff !== undefined && g.telegraf < 0.7) {
      fehler.push(
        `gegner "${g.id}": Flächenangriff mit nur ${g.telegraf}s Vorlauf — Kanon verlangt >= 0.7s`,
      );
    }
  }

  for (const f of i.faehigkeiten.values()) {
    if (f.form === 'geschoss' && (!f.geschoss || !i.geschosse.has(f.geschoss))) {
      fehler.push(`faehigkeit "${f.id}": Form "geschoss" ohne gueltiges Geschoss`);
    }
    if (f.form !== 'selbst' && (f.schaden === undefined || f.reichweite === undefined)) {
      fehler.push(`faehigkeit "${f.id}": ohne Schaden oder Reichweite`);
    }
    if (f.automatikfaehig && (f.eigenbewegung ?? 0) > 0) {
      fehler.push(
        `faehigkeit "${f.id}": automatikfaehig, versetzt aber den Spieler — ` +
          `das nimmt ihm die Bewegung weg`,
      );
    }
    const b = f.standardregel.bedingung;
    if (b === 'mindestens-rang') {
      if (!f.standardregel.rang) {
        fehler.push(`faehigkeit "${f.id}": Bedingung 'mindestens-rang' ohne Rang`);
      }
    } else if (b && f.standardregel.wert === undefined) {
      fehler.push(`faehigkeit "${f.id}": Bedingung '${b}' ohne Wert`);
    }
  }

  for (const a of [...i.praefixe, ...i.suffixe]) {
    // Zahlen-Kanon §12.3: 8 Stufen je Affix
    if (a.stufen.length !== 8) {
      fehler.push(`affix "${a.id}": ${a.stufen.length} Stufen, Kanon verlangt 8`);
    }
    let vorher = -1;
    for (const s of a.stufen) {
      if (s.abPraegelevel <= vorher) {
        fehler.push(`affix "${a.id}": Stufen nicht aufsteigend bei Prägelevel ${s.abPraegelevel}`);
      }
      if (s.min > s.max) fehler.push(`affix "${a.id}": min > max bei Stufe ${s.abPraegelevel}`);
      vorher = s.abPraegelevel;
    }
    if (a.gewicht <= 0) fehler.push(`affix "${a.id}": Gewicht muss > 0 sein`);
    if (a.slots.length === 0) fehler.push(`affix "${a.id}": kein Slot zugeordnet`);
  }

  for (const g of i.gepraegte) {
    if (!i.basen.some((b) => b.id === g.basis)) {
      fehler.push(`gepraegtes "${g.id}": unbekannte Basis "${g.basis}"`);
    }
  }

  // Zahlen-Kanon §12.2: 'schlicht' darf nie auf den Boden fallen.
  const schlicht = i.seltenheiten.find((s) => s.id === 'schlicht');
  if (!schlicht) fehler.push('seltenheiten: "schlicht" fehlt');
  else if (schlicht.faelltAufBoden) {
    fehler.push('seltenheiten: "schlicht" darf nicht auf den Boden fallen (Kanon §12.2)');
  }
  if (i.seltenheiten.length !== 5) {
    fehler.push(`seltenheiten: ${i.seltenheiten.length} Stufen, Kanon verlangt 5`);
  }

  // Regelwerk: die Zusagen, die sich nur durch Zahlen halten lassen.
  const r = i.regelwerk;
  if (r.faehigkeiten.freischaltStufen.length + 1 !== r.faehigkeiten.plaetze) {
    fehler.push(
      `regelwerk: ${r.faehigkeiten.freischaltStufen.length} Freischaltstufen + Startfaehigkeit ` +
        `ergeben nicht ${r.faehigkeiten.plaetze} Plaetze`,
    );
  }
  if (!i.faehigkeiten.has(r.faehigkeiten.startFaehigkeit)) {
    fehler.push(`regelwerk: unbekannte Startfaehigkeit "${r.faehigkeiten.startFaehigkeit}"`);
  }
  for (const rang of RAENGE) {
    if (!r.gegner.raenge[rang]) fehler.push(`regelwerk: Rang "${rang}" fehlt`);
    if (r.beute.chanceJeRang[rang] === undefined) {
      fehler.push(`regelwerk: Beutechance fuer Rang "${rang}" fehlt`);
    }
  }
  // Kanon §12.2: benannte und Elite-Gegner lassen garantiert fallen.
  for (const rang of ['vorarbeiter', 'gezeichneter', 'ausklang'] as const) {
    if (r.beute.chanceJeRang[rang] < 1) {
      fehler.push(`regelwerk: Rang "${rang}" muss garantiert fallen lassen (Kanon §12.2)`);
    }
  }
  // Unverhandelbar: Der Spieler muss sich loesen koennen. Ein Gegner, der
  // schneller ist als er, macht "Bewegung ist die Verteidigung" zur Luege.
  for (const g of i.gegnerListe) {
    if (g.tempo >= r.spieler.tempo) {
      fehler.push(
        `gegner "${g.id}": Tempo ${g.tempo} >= Spielertempo ${r.spieler.tempo} — ` +
          `man kann sich nicht loesen`,
      );
    }
  }
  if (r.karte.gangbreite.min < 3) {
    fehler.push('regelwerk: Gaenge unter 3 Zellen wirken als Schlauch (Ueberarbeitung B1)');
  }

  return fehler;
}
